import { ArrowLeftIcon, LogoFooterSetUpIcon } from '@/components/icons'
import { OPERATIONS_ROUTES } from '@/constant/route'
import useUser from '@/features/auth/hooks/useUser'
import {
  useCommonData,
  useOverAllStatus,
} from '@/features/design-system/hooks/setup'
import { updateInterfaceStatus } from '@/store/slices/interfaceByOrganization/interfaceByOrganizationSlice'
import { useAppDispatch } from '@/store/stores'
import { User } from '@/types/user'
import useSearchInterface from '@features/admin/hooks/interface/useSearchInterface'
import useValidateCurrentInterface from '@features/design-system/hooks/setup/useValidateCurrentInterface'
import { Button, CircularProgress, Stack, Typography } from '@mui/material'
import { useEffect } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import SetUpHeader from '../../../common/SetUpHeader'
import SetUpLayout from '../../../common/SetUpLayout'
import BoxWrapper from '../BoxWrapper'
import classes from './styles.module.scss'

type Props = {}

const SetUpFirst = (props: Props) => {
  // SCRUM-976: pin the setup step to the URL :id (single source of truth), not the global
  // selected interface (AuthContext/localStorage), which a second tab or a stale refresh can change.
  const { id } = useParams()
  const interfaceId = Number(id)

  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { data: user } = useUser<User>()
  const { data: status } = useOverAllStatus(interfaceId)
  const { data: commonDataStatus } = useCommonData(interfaceId)

  const { data: allInterfaces } = useSearchInterface()

  const { isValidInterface, selectedInterface, isLoading } =
    useValidateCurrentInterface({
      currentInterfaceId: id,
      allInterfaces,
      redirectTo: OPERATIONS_ROUTES.index.to,
      autoRedirect: false,
    })

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | undefined

    if (status && status.percentage === 100) {
      // Set a new timeout to navigate after 1 second
      timeoutId = setTimeout(() => {
        // Sync Redux store before navigating to prevent infinite redirect loop:
        // SelectLabelDashboard on the dashboard reads status from Redux (stale false)
        // and would bounce the user straight back here. Same pattern as SetUpSecondV2.
        if (!Number.isNaN(interfaceId)) {
          dispatch(updateInterfaceStatus({ id: interfaceId, status: true }))
        }
        navigate(OPERATIONS_ROUTES.index.to)
      }, 1000)
    }

    // Cleanup the timeout on component unmount
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [status, navigate, dispatch, interfaceId])

  // useEffect(() => {
  //   if (!user) return
  //   if (!setupHelper.isDoneAccountConfig(user)) {
  //     navigate(CONFIG_SETUP_ROUTES.index.to)
  //   }
  // }, [user, navigate])

  return (
    <SetUpLayout
      title="Getting Started"
      header={<SetUpHeader setupFirst={true} />}
      footer={
        <Stack sx={{ color: '#D7D7D7', alignItems: 'center', mb: '20px' }}>
          <LogoFooterSetUpIcon />
        </Stack>
      }
    >
      {status?.percentage === 100 ? (
        <Stack
          flexGrow="1"
          height="100%"
          justifyContent="center"
          alignItems="center"
        >
          <CircularProgress />
        </Stack>
      ) : (
        <>
          {(user?.role === 'SuperAdmin' || user?.role === 'Admin') && (
            <Stack
              direction="row"
              spacing="10px"
              component={Link}
              to={`/admin/interface/`}
              sx={{
                textDecoration: 'none',
                width: '150px',
              }}
            >
              <Button
                variant="secondary"
                sx={{
                  padding: '8px',
                  height: '32px',
                  minWidth: '32px',
                }}
              >
                <ArrowLeftIcon />
              </Button>

              <Stack justifyContent="center">
                <Typography
                  sx={{
                    lineHeight: '115%',
                    fontSize: '12px',
                    fontWeight: '400',
                    color: 'text.main',
                  }}
                >
                  Back to Interface
                </Typography>
              </Stack>
            </Stack>
          )}

          <Stack flexGrow="1" alignItems="center" justifyContent="center">
            <>
              <Typography className={classes.title}>Getting Started</Typography>
              <Typography className={classes.subtitle1}>
                Hi {user?.name}, welcome to Cisgenics Application
              </Typography>
              <Typography color="#999" className={classes.subtitle2}>
                Follow the steps below to setup the site
              </Typography>

              <BoxWrapper
                status={status?.percentage ?? 0}
                commonDataStatus={commonDataStatus?.percentage}
              />
            </>
          </Stack>
        </>
      )}
    </SetUpLayout>
  )
}

export default SetUpFirst
